import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PeakToolbar, { BarAction } from '../PeakToolbar';
import { fmtDateTime } from '../../utils/region';
import {
  coaMasterApi, coaMasterExportName, CoaTemplateRef, CoaImportRecord, CoaSummary, CoaAccount, CoaBrief, CoaView, CoaFirmSummary, CoaKind,
} from '../../utils/api/coamaster';
import CoaChangeLog from './CoaChangeLog';
import { FirmPanel, FirmNote, FirmDialog } from './CoaFirmLayer';

// ── Admin → Master chart template (COA-1) ───────────────────────────────────────────────────────
// The client's 268-account master chart of accounts, as they supplied it, for an admin to read.
// ⛔ THE CUSTOMER'S FILE IS READ-ONLY. No edit, no save, no disabled save on it: the template is loaded
// at boot from the file the customer gave, and replacing it is a person's decision
// (server/src/db/coaMaster.ts), not a form's. The page opens on the customer's file exactly as given.
// ⛔ THE FIRM LAYER (COA-5) sits BESIDE it (./CoaFirmLayer.tsx): a switch to "with the firm's
// judgements" shows the values in force with each judgement's customer value, firm value, reason,
// author, time and source, and the one write on this tab — recording a judgement — goes through the
// server's rules. The export stays the customer's file, whichever view is on.
// ⛔ ADMIN TAB, NOT A WORKSPACE MENU — the company workspace menu was fixed by the owner on 2026-10-13.
// ⛔ EVERY FIGURE IS THE SERVER'S. The counts, the facets and the five observations come from
// GET /api/admin/coa-master/:id, which counts them off the stored rows; nothing here is typed in or
// re-counted. The screen only arranges what it is given (a tree by Parent Account, the server's
// observation lists grouped for reading).
// ⛔ NO RULE OF ITS OWN. What an Account Type word IS (header, control, contra, system, reference,
// posting) is the SERVER's answer — server/src/services/coaRules.ts, sent as `kind` on every row, every
// Account Type facet and every account an observation names. The screen only picks a badge for it;
// it never reads a type word, a code or a name to decide one.
// ⛔ THE CUSTOMER'S WORDS ARE NOT TRANSLATED. Account names (Thai and English), Account Type and
// Subgroup values, Dr / Cr, System / Notes and the README are shown verbatim in every language; only
// the screen's own labels are in src/i18n/mt/coamaster.ts.

/** The kinds that wear a badge. 'posting' and 'unknown' are shown as the plain word. */
type Kind = 'header' | 'control' | 'contra' | 'system' | 'reference';
const KINDS: Kind[] = ['header', 'control', 'contra', 'system', 'reference'];
const badgeOf = (kind: CoaKind | undefined): Kind | '' => ((KINDS as string[]).includes(String(kind)) ? kind as Kind : '');

const KIND_STYLE: Record<Kind, { icon: string; bg: string; fg: string; border: string; tip: string }> = {
  header: { icon: 'ti-folder', bg: 'var(--c-gray-100)', fg: 'var(--text-secondary)', border: '1px dashed var(--border-strong)', tip: 'coam_kind_header' },
  control: { icon: 'ti-lock', bg: '#EFF6FF', fg: '#1D4ED8', border: '1px solid #BFDBFE', tip: 'coam_kind_control' },
  contra: { icon: 'ti-arrows-exchange', bg: '#FFF7ED', fg: '#9A3412', border: '1px solid #FED7AA', tip: 'coam_kind_contra' },
  system: { icon: 'ti-cpu', bg: '#F5F3FF', fg: '#6D28D9', border: '1px solid #DDD6FE', tip: 'coam_kind_system' },
  reference: { icon: 'ti-bookmark', bg: '#ECFDF5', fg: '#047857', border: '1px solid #A7F3D0', tip: 'coam_kind_reference' },
};
/** Which README line speaks to a kind, by the README's own key. Absent → nothing is quoted. */
const KIND_README: Partial<Record<Kind, string>> = {
  header: 'Header accounts', control: 'Control accounts', reference: 'Registered capital',
};

/** The badge an Account Type wears: `kind` is the SERVER's kind of the word (services/coaRules.ts),
 *  the word itself is shown verbatim. 'posting' and 'unknown' wear no badge — the type is plain text.
 *  Presentation only: nothing is filtered or counted by it. */
function TypeBadge({ type, kind }: { type: string; kind: CoaKind }) {
  const { t } = useTranslation();
  const k = badgeOf(kind);
  if (!k) return <span lang="en">{type}</span>;
  const s = KIND_STYLE[k];
  return (
    <span data-kind={k} lang="en" title={t(s.tip)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 7px', borderRadius: 10, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', background: s.bg, color: s.fg, border: s.border }}>
      <i className={`ti ${s.icon}`} style={{ fontSize: 12 }} aria-hidden />{type}
    </span>
  );
}

const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: 16, marginBottom: 14 };
const h2: React.CSSProperties = { fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 };
const muted: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 };
const chipStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'nowrap' };
const field: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' };
const linkBtn: React.CSSProperties = { border: 'none', background: 'none', padding: '2px 4px', color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

/** The customer's README, quoted: every row, every cell, exactly as stored. */
function Readme({ s }: { s: CoaSummary }) {
  const { t } = useTranslation();
  return (
    <section data-section="readme" style={card}>
      <div style={h2}>{t('coam_readme_title')}</div>
      <div style={{ ...muted, marginBottom: 8 }}>{t('coam_readme_lead')}</div>
      <blockquote lang="en" style={{ margin: 0, padding: '8px 12px', borderLeft: '3px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 6 }}>
        {s.readme.map((r, i) => (
          <div key={i} data-readme-row={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 180px) 1fr', gap: 10, padding: '3px 0', fontSize: 12.5 }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r[0]}</span>
            <span style={{ color: 'var(--text-primary)' }}>{r.slice(1).join(' ')}</span>
          </div>
        ))}
      </blockquote>
    </section>
  );
}

/** The five observations the server makes about the file — framed as how the customer's source
 *  IS, never as errors. Each list is the server's; this only lays it out. */
function Observations({ s, nameOf, accountOf }: { s: CoaSummary; nameOf: (a: CoaBrief) => string; accountOf: (code: string) => CoaAccount | undefined }) {
  const { t } = useTranslation();
  const o = s.observations;
  const readme = (key?: string) => (key ? (s.readme.find(r => r[0] === key) || [])[1] || '' : '');
  const quote = (key?: string) => {
    const line = readme(key);
    return line ? (
      <div data-quote={key} style={{ ...muted, marginTop: 6 }}>
        {t('coam_readme_said')} <q lang="en" style={{ color: 'var(--text-primary)' }}>{line}</q>
      </div>
    ) : null;
  };
  const chip = (a: CoaBrief, extra?: React.ReactNode, title?: string) => (
    <span key={a.code} data-code={a.code} title={title} style={chipStyle}>
      <b style={{ fontVariantNumeric: 'tabular-nums' }}>{a.code}</b>
      <span>{nameOf(a)}</span>
      {extra}
    </span>
  );
  // A type with no badge is plain text; beside a name it is set quieter so the two do not run together.
  const typeIn = (a: CoaBrief) => <span style={{ color: 'var(--text-secondary)', fontSize: 11.5 }}><TypeBadge type={a.account_type} kind={a.kind} /></span>;
  const chips = (children: React.ReactNode) => <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>{children}</div>;
  const item = (n: number, title: string, body: React.ReactNode) => (
    <li data-obs={n} style={{ padding: '10px 0', borderTop: n > 1 ? '1px solid var(--border)' : 'none' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{n}. {title}</div>
      {body}
    </li>
  );

  // 3 — grouped by the server's own Account Type value, in the order the server lists them.
  const opposite = new Map<string, CoaSummary['observations']['balanceDiffersFromTop']>();
  for (const r of o.balanceDiffersFromTop) opposite.set(r.account_type, [...(opposite.get(r.account_type) || []), r]);
  const allContra = o.balanceDiffersFromTop.every(r => r.kind === 'contra');

  // 4 — the server's two lists (same English name, same Thai name), shown once per set of codes.
  type Pair = { accounts: CoaSummary['observations']['sameNameEn'][number]['accounts']; en: boolean; th: boolean };
  const pairs = new Map<string, Pair>();
  for (const [list, lang] of [[o.sameNameEn, 'en'], [o.sameNameTh, 'th']] as const) {
    for (const g of list) {
      const key = g.accounts.map(a => a.code).join('/');
      const p = pairs.get(key) || { accounts: g.accounts, en: false, th: false };
      p[lang] = true;
      pairs.set(key, p);
    }
  }
  const pairKind = (p: Pair) => {
    const head = p.accounts.find(a => a.kind === 'header');
    return p.accounts.length === 2 && head && p.accounts.some(a => a !== head && a.parent_code === head.code)
      ? t('coam_obs4_header_child') : t('coam_obs4_separate');
  };

  // 5 — the types the server calls control / system / reference, with the codes the server lists.
  const special = s.facets.accountType.filter(f => ['control', 'system', 'reference'].includes(f.kind));

  return (
    <section data-section="observations" style={card}>
      <div style={h2}>{t('coam_obs_title')}</div>
      <div style={muted}>{t('coam_obs_lead')}</div>
      <ol style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
        {item(1, t('coam_obs1_title'), (
          <>
            <div style={muted}>
              {o.headers.withoutChildren.length === 0
                ? t('coam_obs1_all', { total: o.headers.total })
                : t('coam_obs1_some', { with: o.headers.withChildren, total: o.headers.total })}
            </div>
            {o.headers.withoutChildren.length > 0 && chips(o.headers.withoutChildren.map(a => chip(a)))}
            {quote(KIND_README.header)}
          </>
        ))}
        {item(2, t('coam_obs2_title'), o.nonHeaderParents.length === 0 ? <div style={muted}>{t('coam_obs2_none')}</div> : (
          <>
            <div style={muted}>{t('coam_obs2_text', { n: o.nonHeaderParents.length })}</div>
            {chips(o.nonHeaderParents.map(a => chip(a, <>
              {typeIn(a)}
              <span style={{ color: 'var(--text-tertiary)' }}>{t('coam_sub_count', { count: a.children })}</span>
            </>)))}
            <div style={{ ...muted, marginTop: 6 }}>{t('coam_obs2_note')}</div>
            {/* The firm's answer to this observation (COA-5): how many of these accounts carry a judgement
                on their type that still stands — the server's tally, not one made here. */}
            {s.firm && (
              <div data-obs2-judged style={{ ...muted, marginTop: 6, color: 'var(--text-primary)' }}>
                <i className="ti ti-scale" aria-hidden /> {t('coam_obs2_judged', { judged: s.firm.parents.judged, total: s.firm.parents.total })}
              </div>
            )}
          </>
        ))}
        {item(3, t('coam_obs3_title'), o.balanceDiffersFromTop.length === 0 ? <div style={muted}>{t('coam_obs3_none')}</div> : (
          <>
            <div style={muted}>{t('coam_obs3_text', { n: o.balanceDiffersFromTop.length })}</div>
            {Array.from(opposite.entries()).map(([type, list]) => (
              <div key={type} data-obs3-type={type} style={{ marginTop: 6 }}>
                <TypeBadge type={type} kind={list[0].kind} /> <span style={{ ...muted, fontSize: 12 }}>× {list.length}</span>
                {chips(list.map(a => chip(a, <span lang="en" style={{ fontWeight: 700 }}>{a.normal_balance}</span>,
                  t('coam_opposite_tip', { side: a.normal_balance, top: a.top_code, topSide: a.top_balance }))))}
              </div>
            ))}
            <div style={{ ...muted, marginTop: 6 }}>{allContra ? t('coam_obs3_all_contra') : t('coam_obs3_some_contra')}</div>
          </>
        ))}
        {item(4, t('coam_obs4_title'), pairs.size === 0 ? <div style={muted}>{t('coam_obs4_none')}</div> : (
          <>
            <div style={muted}>{t('coam_obs4_text', { en: o.sameNameEn.length, th: o.sameNameTh.length })}</div>
            {Array.from(pairs.entries()).map(([key, p]) => (
              <div key={key} data-obs4-pair={key} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 6 }}>
                {p.accounts.map(a => chip(a, typeIn(a)))}
                <span style={{ ...muted, fontSize: 12 }}>
                  {pairKind(p)} · {p.en && p.th ? t('coam_obs4_same_both') : p.en ? t('coam_obs4_same_en') : t('coam_obs4_same_th')}
                </span>
              </div>
            ))}
          </>
        ))}
        {item(5, t('coam_obs5_title'), special.length === 0 ? <div style={muted}>{t('coam_obs5_none')}</div> : (
          <>
            <div style={muted}>{t('coam_obs5_text')}</div>
            {special.map(f => (
              <div key={f.value} data-obs5-type={f.value} style={{ marginTop: 8 }}>
                <TypeBadge type={f.value} kind={f.kind} />
                {/* The codes are the server's (facets.accountType[].codes); the names are looked up in the rows already on the page. */}
                {chips(f.codes.map(code => { const a = accountOf(code); return chip(a || { code, name_th: '', name_en: '', account_type: f.value, kind: f.kind }); }))}
                {quote(KIND_README[f.kind as Kind])}
              </div>
            ))}
          </>
        ))}
      </ol>
    </section>
  );
}

export default function CoaMaster() {
  const { t, i18n } = useTranslation();
  const [list, setList] = useState<{ templates: CoaTemplateRef[]; imports: CoaImportRecord[] } | null>(null);
  const [tid, setTid] = useState('');
  const [sum, setSum] = useState<CoaSummary | null>(null);
  const [all, setAll] = useState<CoaAccount[] | null>(null);         // the whole template, for the tree
  const [hits, setHits] = useState<{ rows: CoaAccount[]; matched: number } | null>(null);   // a search's answer
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [err, setErr] = useState('');
  const [exporting, setExporting] = useState(false);
  // COA-5: which values the table shows — the customer's file (the default) or the values in force with
  // the firm's judgements — the row being ruled on, and a counter that reloads the rows after a ruling.
  const [view, setView] = useState<CoaView>('customer');
  const [ruling, setRuling] = useState<CoaAccount | null>(null);
  const [reload, setReload] = useState(0);
  const seq = useRef(0);
  const filtered = !!(q.trim() || category || type);

  useEffect(() => {
    coaMasterApi.list()
      .then(d => { setList(d); setTid(d.templates[0]?.id || ''); })
      .catch((e: any) => setErr(String(e?.message || e)));
  }, []);
  useEffect(() => {
    if (!tid) return;
    let alive = true;   // a template picked and then another: the first one's answers are dropped
    setSum(null); setAll(null); setHits(null); setErr(''); setView('customer');
    coaMasterApi.get(tid).then(d => { if (alive) setSum(d); }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    coaMasterApi.accounts(tid).then(d => {
      if (!alive) return;
      setAll(d.rows);
      // Opens on the top-level accounts and the level under them; everything else one click away.
      setExpanded(new Set(d.rows.filter(r => r.depth === 0).map(r => r.code)));
    }).catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [tid]);
  // The other view, or the same one again after a ruling: the rows are asked for again, as the server
  // composes them. The tree keeps whatever is open — the codes are the same in both views.
  const firstRows = useRef(true);
  useEffect(() => {
    if (firstRows.current) { firstRows.current = false; return; }
    if (!tid) return;
    let alive = true;
    coaMasterApi.accounts(tid, { view }).then(d => { if (alive) setAll(d.rows); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [view, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  // The search box asks the server after a pause in typing, not on every key.
  useEffect(() => { const h = setTimeout(() => setQ(typed), 200); return () => clearTimeout(h); }, [typed]);
  useEffect(() => {
    const my = ++seq.current;
    if (!tid || !filtered) { setHits(null); return; }
    coaMasterApi.accounts(tid, { q, category, type, view }).then(d => {
      if (my !== seq.current) return;   // a later question has been asked; this answer is stale
      setHits({ rows: d.rows, matched: d.matched });
      // Every match is shown: the tree opens all the way down to it.
      const present = new Set(d.rows.map(r => r.code));
      setExpanded(new Set(d.rows.filter(r => r.parent_code && present.has(r.parent_code)).map(r => r.parent_code)));
    }).catch((e: any) => { if (my === seq.current) setErr(String(e?.message || e)); });
  }, [tid, q, category, type, view, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  // Back to the opening view when the last filter is cleared.
  const wasFiltered = useRef(false);
  useEffect(() => {
    if (wasFiltered.current && !filtered && all) setExpanded(new Set(all.filter(r => r.depth === 0).map(r => r.code)));
    wasFiltered.current = filtered;
  }, [filtered, all]);

  const rows: CoaAccount[] | null = filtered ? (hits ? hits.rows : null) : all;
  const tree = useMemo(() => {
    const present = new Set((rows || []).map(r => r.code));
    const kids = new Map<string, CoaAccount[]>();
    const roots: CoaAccount[] = [];
    for (const r of rows || []) {
      if (r.parent_code && present.has(r.parent_code)) kids.set(r.parent_code, [...(kids.get(r.parent_code) || []), r]);
      else roots.push(r);
    }
    return { kids, roots };
  }, [rows]);
  const visible = useMemo(() => {
    const out: CoaAccount[] = [];
    const walk = (r: CoaAccount) => { out.push(r); if (expanded.has(r.code)) for (const k of tree.kids.get(r.code) || []) walk(k); };
    tree.roots.forEach(walk);
    return out;
  }, [tree, expanded]);
  const opposite = useMemo(() => new Map((sum?.observations.balanceDiffersFromTop || []).map(r => [r.code, r])), [sum]);

  const toggle = (code: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });
  const expandAll = () => setExpanded(new Set(Array.from(tree.kids.keys())));
  const collapseAll = () => setExpanded(new Set());
  const clear = () => { setTyped(''); setQ(''); setCategory(''); setType(''); };
  const nameOf = (a: CoaBrief) => (i18n.language === 'th' ? a.name_th : a.name_en);
  const byCode = useMemo(() => new Map((all || []).map(r => [r.code, r])), [all]);
  const accountOf = (code: string) => byCode.get(code);

  /** A judgement was recorded: the layer the server now reports, and the rows composed again. */
  const ruled = (firm: CoaFirmSummary) => {
    setSum(prev => (prev ? { ...prev, firm } : prev));
    setRuling(null);
    setReload(n => n + 1);
  };
  const firmView = view === 'firm';

  const doExport = async () => {
    if (!sum || exporting) return;
    setExporting(true); setErr('');
    try { await coaMasterApi.exportXlsx(sum.id, coaMasterExportName(sum.sourceFile)); }
    catch (e: any) { setErr(String(e?.message || e)); }
    finally { setExporting(false); }
  };

  const errBox = err ? (
    <div role="alert" style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>
      {t('coam_error', { error: err })}
    </div>
  ) : null;

  if (list && list.templates.length === 0) {
    const last = list.imports[0];
    return (
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        {errBox}
        <section data-section="none" style={card}>
          <div style={h2}>{t('coam_title')}</div>
          <div style={muted}>{t('coam_none')}</div>
          {last && <div style={{ ...muted, marginTop: 8 }}>{t('coam_last_record')} <q lang="en">{last.reason}</q></div>}
        </section>
        {/* COA-10: the change log is of every CLIENT's chart and the house template, which exist whether or
            not a master template is loaded — so it stays reachable here, the one place it is shown. */}
        <CoaChangeLog />
      </div>
    );
  }
  if (!sum || !all) {
    return (
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        {errBox}
        {!err && <div style={{ ...muted, padding: 16, textAlign: 'center' }}><i className="ti ti-loader" aria-hidden /> {t('coam_loading')}</div>}
      </div>
    );
  }

  const c = sum.counts;
  const tiles: [string, string, number, string?][] = [
    ['accounts', t('coam_count_accounts'), c.accounts],
    ['mainCategories', t('coam_count_categories'), c.mainCategories],
    ['subgroups', t('coam_count_subgroups'), c.subgroups],
    ['accountTypes', t('coam_count_types'), c.accountTypes],
    ['maxDepth', t('coam_count_depth'), c.maxDepth, t('coam_depth_hint', { n: c.maxDepth })],
  ];
  const actions: BarAction[] = [{
    key: 'export', label: t('coam_export'), icon: 'ti-download', busy: exporting,
    title: firmView ? t('coam_export_hint_firm', { n: c.accounts }) : t('coam_export_hint', { n: c.accounts }), onClick: doExport,
  }];
  const cols: [string, number][] = [
    ['coam_col_code', 0], ['coam_col_th', 1], ['coam_col_en', 2], ['coam_col_type', 5],
    ['coam_col_subgroup', 4], ['coam_col_side', 7], ['coam_col_notes', 8],
  ];
  const legend = KINDS.map(k => ({ k, types: sum.facets.accountType.filter(f => f.kind === k) })).filter(x => x.types.length);

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto' }}>
      {errBox}
      <section data-section="source" style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
          <i className="ti ti-sitemap" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('coam_title')}</div>
          <div lang="en" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{sum.name}</div>
          <div style={{ flex: 1 }} />
          {list && list.templates.length > 1 && (
            <select aria-label={t('coam_template')} value={tid} onChange={e => { clear(); setTid(e.target.value); }} style={field}>
              {list.templates.map(x => <option key={x.id} value={x.id}>{x.name} — {x.sourceFile}</option>)}
            </select>
          )}
        </div>
        <div style={{ ...muted, marginBottom: 10 }}>{t('coam_readonly')}</div>
        <div data-provenance style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <span>{t('coam_src_file')}: <b data-prov="file" style={{ color: 'var(--text-primary)' }}>{sum.sourceFile}</b></span>
          <span>{t('coam_src_sheet')}: <span data-prov="sheet" lang="en" style={{ color: 'var(--text-primary)' }}>{sum.sourceSheet}</span></span>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{t('coam_src_sha')}: <code data-prov="sha" style={{ fontSize: 11.5, color: 'var(--text-primary)' }}>{sum.sourceSha256}</code></span>
          <span>{t('coam_src_imported')}: <span data-prov="imported" style={{ color: 'var(--text-primary)' }}>{fmtDateTime(sum.importedAt)}</span>
            {/* The stored actor stays exact for the audit trail ('boot seed (db/coaMasterData.ts)' when the
                template arrived with the code); the SCREEN says it in words a person reads. */}
            {sum.importedBy && <> {/^boot seed\b/.test(sum.importedBy) ? t('coam_src_by_system') : t('coam_src_by', { who: sum.importedBy })}</>}</span>
          <span>{t('coam_src_rows')}: <b data-prov="rows" style={{ color: 'var(--text-primary)' }}>{sum.rowCount}</b></span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          {tiles.map(([key, label, n, hint]) => (
            <div key={key} data-count={key} title={hint} style={{ minWidth: 112, padding: '8px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-app)' }}>
              <div data-count-value style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{n}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{label}</div>
            </div>
          ))}
        </div>
        <div data-count="more" style={{ ...muted, fontSize: 12, marginTop: 8 }}>
          {t('coam_count_more', { top: c.topLevel, dr: c.normalBalance.Dr ?? 0, cr: c.normalBalance.Cr ?? 0, notes: c.withNotes })}
        </div>
      </section>

      {sum.firm && <FirmPanel firm={sum.firm} view={view} onView={setView} />}

      <section data-section="accounts" data-view={view} style={{ ...card, padding: 0, overflow: 'visible' }}>
        <PeakToolbar
          filters={<>
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <i className="ti ti-search" aria-hidden style={{ position: 'absolute', left: 8, fontSize: 14, color: 'var(--text-tertiary)' }} />
              <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={t('coam_search_ph')} aria-label={t('coam_search_ph')}
                style={{ ...field, width: 300, paddingLeft: 28 }} />
            </span>
            <select aria-label={t('coam_filter_category')} value={category} onChange={e => setCategory(e.target.value)} style={field}>
              <option value="">{t('coam_all_categories')}</option>
              {sum.facets.mainCategory.map(f => <option key={f.value} value={f.value}>{f.value} ({f.n})</option>)}
            </select>
            <select aria-label={t('coam_filter_type')} value={type} onChange={e => setType(e.target.value)} style={field}>
              <option value="">{t('coam_all_types')}</option>
              {sum.facets.accountType.map(f => <option key={f.value} value={f.value}>{f.value} ({f.n})</option>)}
            </select>
            {(filtered || typed) && <button onClick={clear} style={{ ...linkBtn, whiteSpace: 'nowrap' }}>{t('coam_clear')}</button>}
          </>}
          actions={actions}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
          <span data-showing style={{ ...muted, fontSize: 12 }}>
            {filtered
              ? (hits ? (hits.matched ? t('coam_showing_match', { matched: hits.matched, n: c.accounts }) : t('coam_no_match')) : t('coam_loading'))
              : t('coam_showing_all', { n: c.accounts })}
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={expandAll} style={linkBtn}>{t('coam_expand_all')}</button>
          <button onClick={collapseAll} style={linkBtn}>{t('coam_collapse_all')}</button>
        </div>
        <div data-legend style={{ display: 'flex', alignItems: 'center', gap: '6px 14px', flexWrap: 'wrap', padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)' }}>
          <span style={{ fontWeight: 700 }}>{t('coam_legend')}:</span>
          {legend.map(({ k, types }) => (
            <span key={k} data-legend-kind={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {types.map(f => <TypeBadge key={f.value} type={f.value} kind={f.kind} />)}
              <span>{t(KIND_STYLE[k].tip)}</span>
            </span>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table role="treegrid" aria-label={t('coam_title')} style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
            <thead><tr>
              {cols.map(([k, i]) => <th key={k} title={sum.header[i]} style={th}>{t(k)}</th>)}
              {firmView && <th data-firm-head style={th}>{t('coam_col_firm')}</th>}
            </tr></thead>
            <tbody>
              {visible.map(r => {
                const n = tree.kids.get(r.code)?.length || 0;
                const open = expanded.has(r.code);
                const ctx = !r.match;
                const opp = opposite.get(r.code);
                const ink = ctx ? 'var(--text-tertiary)' : 'var(--text-primary)';
                // In the firm view, a cell whose value is the FIRM's (not the customer's) is marked.
                const ov = (f: 'name_th' | 'name_en' | 'account_type' | 'subgroup' | 'system_notes') =>
                  (firmView && r.firm?.cells?.[f]?.overridden ? '1' : undefined);
                const ovStyle = (f: 'name_th' | 'name_en' | 'account_type' | 'subgroup' | 'system_notes'): React.CSSProperties =>
                  (ov(f) ? { boxShadow: 'inset 3px 0 0 var(--accent)' } : {});
                return (
                  <tr key={r.code} data-code={r.code} data-match={r.match ? '1' : '0'} aria-level={r.depth + 1}
                    aria-expanded={n ? open : undefined} title={ctx ? t('coam_context_tip') : undefined}>
                    <td style={{ ...td, color: ink, whiteSpace: 'nowrap', paddingLeft: 6 + r.depth * 18, fontVariantNumeric: 'tabular-nums' }}>
                      {n ? (
                        <button onClick={() => toggle(r.code)} aria-expanded={open}
                          aria-label={open ? t('coam_collapse', { code: r.code }) : t('coam_expand', { code: r.code })}
                          title={open ? t('coam_collapse', { code: r.code }) : t('coam_expand', { code: r.code })}
                          style={{ width: 20, height: 20, padding: 0, marginRight: 4, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', verticalAlign: 'middle' }}>
                          <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 14 }} aria-hidden />
                        </button>
                      ) : <span style={{ display: 'inline-block', width: 24 }} />}
                      <span style={{ fontWeight: r.kind === 'header' ? 700 : 400 }}>{r.code}</span>
                    </td>
                    <td lang="th" data-overridden={ov('name_th')} style={{ ...td, color: ink, ...ovStyle('name_th') }}>{r.name_th}</td>
                    <td lang="en" data-overridden={ov('name_en')} style={{ ...td, color: ink, ...ovStyle('name_en') }}>{r.name_en}</td>
                    <td data-overridden={ov('account_type')} style={{ ...td, opacity: ctx ? 0.6 : 1, ...ovStyle('account_type') }}>
                      <TypeBadge type={r.account_type} kind={r.kind} />
                      {firmView && r.firm?.controlRole && <span data-control-role={r.firm.controlRole} title={t('coam_firm_field_control_role')}
                        style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#1D4ED8' }}><i className="ti ti-lock" aria-hidden /> {r.firm.controlRole === 'ar' ? t('coam_firm_role_ar') : t('coam_firm_role_ap')}</span>}
                    </td>
                    <td lang="en" data-overridden={ov('subgroup')} style={{ ...td, color: ink, ...ovStyle('subgroup') }}>{r.subgroup}</td>
                    <td lang="en" style={{ ...td, color: ink, whiteSpace: 'nowrap' }}>
                      {r.normal_balance}
                      {opp && <i className="ti ti-arrows-exchange" data-opposite={r.code} style={{ fontSize: 12, marginLeft: 4, color: '#9A3412' }}
                        title={t('coam_opposite_tip', { side: opp.normal_balance, top: opp.top_code, topSide: opp.top_balance })} aria-hidden />}
                    </td>
                    <td lang="en" data-overridden={ov('system_notes')} style={{ ...td, color: ink, fontSize: 12, ...ovStyle('system_notes') }}>{r.system_notes}</td>
                    {firmView && <td style={{ ...td, fontSize: 12 }}><FirmNote row={r} onRule={setRuling} /></td>}
                  </tr>
                );
              })}
              {rows && visible.length === 0 && (
                <tr><td colSpan={cols.length + (firmView ? 1 : 0)} style={{ ...td, color: 'var(--text-tertiary)' }}>{t('coam_no_match')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Observations s={sum} nameOf={nameOf} accountOf={accountOf} />
      <Readme s={sum} />
      {/* COA-10: the change log of the charts this template does NOT feed — every client's chart and
          the house template — with its two integrity counts. Its own component and its own call. */}
      <CoaChangeLog refreshKey={reload} />
      {ruling && sum.firm && (
        <FirmDialog templateId={sum.id} row={ruling} firm={sum.firm} onClose={() => setRuling(null)} onSaved={ruled} />
      )}
    </div>
  );
}
